import type {
  StoreStockTrackingItem,
  StoreStockTrackingVariant
} from "@repo/api-sdk";

import { isAddable, type ReorderLine } from "@/lib/reorder-diff";
import { resolveVariantTitle } from "@/utils/variant";

/**
 * The **stock-item** module: the single owner of the storefront's rules about a
 * tracked stock item (#71-#73, over api#148/#152). A salon opts a variant into
 * tracking with a `reorder_level` and an optional `on_hand` count; this module
 * owns the "due to reorder" predicate, the level/count input parsing, the
 * display derivation off the hydrated variant, and the small pure selections
 * every stock surface shares.
 *
 * Every stock surface - the My Stock editor, the seed + count dialogs, the home
 * Due-to-Reorder widget - consumes this interface rather than re-deriving the
 * rules. It is the sibling of `reorder-diff`: normalize/derive once here, and
 * speak this vocabulary at the call sites. Before this module the reorder-level
 * parse had already diverged across two dialogs; keeping one rule here is the
 * point.
 */

// ============================================================
// Due predicate (mirrors the server's read-time is_due)
// ============================================================

/**
 * `on_hand` is set AND `on_hand <= reorder_level`. A blank (`null`) on-hand is
 * never due - an uncounted item can't be flagged low. This mirrors the server's
 * read-time `is_due`: the wire flag stays authoritative, and this client copy is
 * what the optimistic list patch recomputes so the hero stat can't flash a stale
 * number.
 */
export function isDue(reorderLevel: number, onHand: number | null): boolean {
  return onHand !== null && onHand <= reorderLevel;
}

// ============================================================
// Input parsing (one rule, a caller-supplied fallback)
// ============================================================

/**
 * Parse a reorder-level input. A blank or unparseable value falls back to
 * `fallback` - the current level for an inline edit, or the seed default of `1`
 * for a new track. A valid `>= 0` integer is taken as-is: `0` is a legitimate
 * level ("only flag me when I'm out"), never silently bumped to `1`.
 */
export function parseReorderLevel(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

/**
 * Parse an on-hand input. A blank value means "not counted" (`null`, rendered
 * distinctly from `0`); an unparseable value keeps `fallback` (the current
 * count); a valid `>= 0` integer is taken as-is.
 */
export function parseOnHand(
  raw: string,
  fallback: number | null
): number | null {
  if (raw.trim() === "") {
    return null;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

// ============================================================
// Display derivation (from the hydrated variant)
// ============================================================

/** Anything the routes hydrate with a tracked variant: an item or a candidate. */
type WithVariant = { variant: StoreStockTrackingVariant | null };

/**
 * The primary display title for a tracked variant: the product title, else the
 * variant title, else a generic fallback (a `null` variant means the catalog no
 * longer resolves it).
 *
 * The variant fallback is filtered for Medusa's placeholder: a product with no
 * real options carries "Default variant", which would otherwise become the
 * heading here and name nothing at all.
 */
export function stockItemTitle(item: WithVariant): string {
  return (
    item.variant?.product?.title ??
    resolveVariantTitle(item.variant?.title) ??
    "Unavailable product"
  );
}

/**
 * The variant identity to show as a secondary pill (e.g. shade / size) - only
 * when it adds information beyond {@link stockItemTitle}: present, a real
 * option rather than Medusa's placeholder, and not equal to the title already
 * shown. `null` when it would just duplicate or say nothing.
 */
export function stockItemVariantLabel(item: WithVariant): string | null {
  const title = stockItemTitle(item);
  const variantTitle = resolveVariantTitle(item.variant?.title);
  return variantTitle && variantTitle !== title ? variantTitle : null;
}

// ============================================================
// Selections (pure derivations over a tracked-item set)
// ============================================================

/** One tracked item paired with the new on-hand its edited input parses to. */
export interface CountEdit {
  item: StoreStockTrackingItem;
  on_hand: number | null;
}

/**
 * Given tracked items and the raw on-hand inputs keyed by `variant_id`, the
 * subset whose count actually changed, each with its parsed new `on_hand`. An
 * unparseable input keeps the current count (via {@link parseOnHand}), so it is
 * never treated as a change - the caller upserts only what a salon really edited.
 */
export function changedCounts(
  items: StoreStockTrackingItem[],
  rawByVariantId: Record<string, string>
): CountEdit[] {
  const edits: CountEdit[] = [];
  for (const item of items) {
    const on_hand = parseOnHand(
      rawByVariantId[item.variant_id] ?? "",
      item.on_hand
    );
    if (on_hand !== item.on_hand) {
      edits.push({ item, on_hand });
    }
  }
  return edits;
}

/**
 * The tracked items actually added to a cart by a reorder: those whose variant
 * resolved to an addable ({@link isAddable}) line. Drives the post-reorder count
 * prompt, which should only nudge for items that landed in a cart.
 */
export function addedItems(
  items: StoreStockTrackingItem[],
  lines: ReorderLine[]
): StoreStockTrackingItem[] {
  const addable = new Set(
    lines.filter(isAddable).map((line) => line.variant_id)
  );
  return items.filter((item) => addable.has(item.variant_id));
}
